import React, { useContext, useEffect, useState } from "react";
import { Box, Flex, Grid } from "theme-ui";
import SelectInput from "components/forms/SelectInput";
import { Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import { getClients } from "api/Clients";
import GooglePlaces from "components/forms/GooglePlaces";
import TextInput from "components/forms/TextInput";
import BasicButton from "components/forms/BasicButton";
import { SubmitIcon } from "components/svg";
import { themevals } from "theme/themevals";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import { InstantEstimateIcon, ClientsIcon, LocationIcon } from "components/svg";
import RFQIcon from "components/svg/icons/RFQIcon";
import Subtext from "components/typography/Subtext";
import P from "components/typography/P";
import Checkbox from "components/forms/Checkbox";
import SelectDate from "components/forms/SelectDate";
import { Button } from "theme-ui";
import { CheckMarkIcon, QuotesIcon } from "components/svg";
import { getProduct } from "api/Products";
import { createEstimate, updateEstimate, getEstimateSettings, getEstimateMinimum } from "api/InstantEstimate";
import { getFuelSurchargeActive } from "api/FuelSurcharge";
import { trackActivity } from "api/ActivityTracker";
import { useToast } from "components/toast";
import UserContext from "context/User";
import { getProducts } from "api/Products";
import moment from "moment";
import { generateQuoteFromEstimate } from "api/QuoteRequests";
import { convertWorkOrder } from "api/ServicePlans";
import { useNavigate } from "react-router-dom";
import { createNote } from "api/Notes";
import { useTableRefresh } from "context/TableRefresh";
import { getInstantEstimateCombos } from "api/Companies";
import Span from "components/typography/Span";

interface Address {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
}

interface Location {
  id: string;
  name: string;
  address: Address;
}

interface Service {
  id: string;
  name: string;
  description?: string;
  quantity: number;
  baseCost: string;
  lowCost: string;
  highCost: string;
  markup: string;
  distancePercentage: string;
  price: string;
  position: number;
}

interface Rfq {
  id: string;
  location: Location;
  services: Service[];
  quoteRequest: { sequence: number; id: string };
  estimate: any;
}

interface UpdateEstimateData {
  projectName?: string;
  serviceDate?: string;
  instructions?: string;
  addons?: { id: string; quantity: number }[];
  primaryClientId?: string;
  selectServices?: string[];
  quantity?: number;
  mulchSupplyPreference?: 'pickup' | 'delivery' | null;
}

// Install-only detection. The service name is operator-created (via the pricing
// importer or the Add Service form), so its casing is not guaranteed - production
// has it as "Install only" while earlier code assumed "Install Only". Match
// case-insensitively on the trimmed name so the install-only behavior (supply
// preference gating, list ordering, the pill label) is not broken by casing.
const isInstallOnlyName = (name?: string | null) =>
  (name ?? "").trim().toLowerCase() === "install only";

/**
 * Keeps the form's "Min N" in step with the client, site and product picked
 * (B6): the client override or company minimum, raised by depot minimums
 * when every depot covering the site has one. Renders nothing.
 */
const MinimumWatcher: React.FC<{ isCompany: boolean; onMin: (n: number | null) => void }> = ({ isCompany, onMin }) => {
  const { values } = useFormikContext<any>();
  const clientId = isCompany ? values?.company ?? null : null;
  const { latitude, longitude, bulk_product: productId } = values ?? {};
  React.useEffect(() => {
    let live = true;
    getEstimateMinimum({ clientId, latitude, longitude, productId }).then((m) => {
      if (live && m !== undefined) onMin(m);
    });
    return () => { live = false; };
  }, [clientId, latitude, longitude, productId]); // eslint-disable-line
  return null;
};

const InstantEstimateTool = ({ isCompany }: any) => {
  const [companyOptions, setCompanyOptions] = useState<{ value: string; label: string }[]>([]);
  const [productOptions, setProductOptions] = useState<{ value: string; label: string }[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showEstimateFormModal, setShowEstimateFormModal] = useState<boolean>(false);
  const [unit, setUnit] = useState<string>("");
  // Open-RFQ prompt: set when create() answers 409 OPEN_RFQ_EXISTS.
  const [dupPrompt, setDupPrompt] = useState<{ values: any; actions: any; existing: any; matches: any[] } | null>(null);
  // Which open RFQ "Update existing" targets when there are several here.
  const [dupPick, setDupPick] = useState<string | null>(null);
  // Open-work-order prompt (staff): set when create() answers 409
  // OPEN_WORK_ORDER. Pricing it runs the work order's own Generate RFQ, so
  // the RFQ is created linked to the work order by id.
  const [woPrompt, setWoPrompt] = useState<{ values: any; actions: any; matches: any[] } | null>(null);
  const [woPick, setWoPick] = useState<string | null>(null);
  const [woBusy, setWoBusy] = useState<boolean>(false);
  // Per-option draft for "Requires at least N yd" (option id -> typed yards).
  const [minDraft, setMinDraft] = useState<Record<string, string>>({});
  // RFQ number for messages, e.g. GMR-00100.
  const rfqLabel = (r: any) => (r?.quoteRequest?.sequence != null ? `GMR-${String(r.quoteRequest.sequence).padStart(5, "0")}` : "the RFQ");
  // Company minimum (yards) for an instant estimate; null = none. The
  // server enforces it too; this only says it up front.
  const [minQtyYds, setMinQtyYds] = useState<number | null>(null);
  // Fuel surcharge notice: one small line while the surcharge is on - no %
  // and no amount; the amount appears only on the generated quote (Jack).
  const [fuelActive, setFuelActive] = useState<boolean>(false);
  React.useEffect(() => {
    getEstimateSettings().then((s) => setMinQtyYds(s.minQuantityYds));
    getFuelSurchargeActive().then(setFuelActive);
  }, []);
  const isYards = (u: string) => ["yds", "yd", "yard", "yards", "cy", ""].includes(String(u || "").toLowerCase());
  const minHint = minQtyYds && isYards(unit) ? `Min ${minQtyYds}` : undefined;
  const [bulkProduct, setBulkProduct] = useState<any>(null);
  const [rfq, setRfq] = useState<Rfq | null>(null);
  const [client, setClient] = useState<any>(null);
  const [companyClientOptions, setCompanyClientOptions] = useState<{ value: string; label: string }[]>([]);
  const [rfqSubmitting, setRfqSubmitting] = useState<boolean>(false);
  const [terms, setTerms] = useState<any>(null);
  const [initialRfqValues, setInitialRfqValues] = useState<any>(null);

  const toast = useToast();
  const { user } = useContext<any>(UserContext);
  const navigate = useNavigate();
  const { triggerRefresh } = useTableRefresh();

  const _submitInstantEstimate = async (values: any, actions: any, choice?: { duplicateAction: "update" | "new"; existingEstimateId?: string }) => {
    if (minQtyYds && isYards(unit) && Number(values.quantity || 0) < minQtyYds) {
      toast.add(`The minimum order for an instant estimate is ${minQtyYds} cubic yards.`, "var(--theme-ui-colors-danger)");
      return;
    }
    const data = {
      address: values.address,
      latitude: values.latitude,
      longitude: values.longitude,
      productId: values.bulk_product,
      quantity: values.quantity,
      // The product's unit, so the server can tell yards from sqft.
      ...(unit ? { unit } : {}),
      // The answer to the "open RFQ already exists" prompt, on the resubmit.
      ...(choice ? choice : {}),
      // "Not for this work order" from the open-work-order prompt.
      ...(values?.workOrderAction ? { workOrderAction: values.workOrderAction } : {}),
    } as any;

    if (isCompany) {
      data.clientId = values.company;
    }

    const res = await createEstimate({ data: data });

    // This client has an open work order at this site: price the work
    // order (linked by id) or say this RFQ is separate.
    if (res?.statusCode === 409 && res?.code === "OPEN_WORK_ORDER" && Array.isArray(res?.matches) && res.matches.length) {
      setWoPrompt({ values, actions, matches: res.matches });
      setWoPick(res.matches[0]?.id ?? null);
      return;
    }

    // The organization already has an open RFQ for this site + product:
    // ask before doing anything (update that one / create a new one).
    if (res?.statusCode === 409 && res?.code === "OPEN_RFQ_EXISTS" && res?.existing) {
      const matches: any[] = Array.isArray(res.matches) && res.matches.length ? res.matches : [res.existing];
      setDupPrompt({ values, actions, existing: res.existing, matches });
      setDupPick(matches.find((m) => m.updatable !== false)?.estimateId ?? null);
      return;
    }

    if (res?.statusCode && res.statusCode >= 400) {
      toast.add(res.message || "An error occurred while creating the estimate.", "var(--theme-ui-colors-danger)");
      return;
    }

    // Track quote request creation (instant estimate flow)
    void trackActivity({
      module: "quote-requests",
      action: "create_quote_request",
      actionType: "create",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "quote-request",
      entityId: res.quoteRequest?.id || null,
      metadata: {
        clientId: isCompany ? values.company : res.client?.id || null,
      },
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });

    setTerms(res?.client?.terms || null);

    if (isCompany) {
      const clientRes = await getClients({ query: { filters: { "parent.id": `${client.value}:is` } } });
      setCompanyClientOptions(clientRes.items.map((p: any) => ({ value: p.id, label: p.name })));
    } else {
      setCompanyClientOptions([{ value: user.id, label: `${user.firstName} ${user.lastName}` }]);
      setClient({ value: res.client.id, label: res.client.name });
    }

    setRfq(res);

    // Store initial RFQ values for comparison
    setInitialRfqValues({
      projectName: res.projectName || null,
      quantity: res.estimate?.selectedService?.quantity || res.estimate?.services?.[0]?.quantity || null,
      serviceDate: res.serviceDate || null,
      instructions: res.instructions || null,
      primaryClientId: res.primaryClient?.id || null,
      selectServices: res.estimate?.selectedService?.id || null,
    });

    if (actions) {
      actions.resetForm();
      actions.setFieldValue("googlePlace", "");
    }
    toggleModal();
  };

  useEffect(() => {
    const init = async () => {
      if (isCompany) {
        const res = await getClients({ query: { filters: { type: "2:is" }, recordsPer: 1000 } });
        if (res.items && res.items.length > 0) {
          const compOpt = res.items.map((p: any) => ({ value: p.id, label: p.name }));
          setCompanyOptions(compOpt);
        } else {
          setCompanyOptions([]);
        }
      } else {
        setCompanyOptions([]);
      }

      const comboRes = await getInstantEstimateCombos();
      // After fetching combos, map to product options from comboRes
      const prodOpt = comboRes.items.map((combo: any) => ({
        value: combo.product.id,
        label: combo.product.name,
      }));
      setProductOptions(prodOpt);
    };
    init();
  }, []);

  const toggleModal = (values?: any) => {
    // Update-existing preview (2026-10-07): closing saves NOTHING, for staff
    // and clients alike, and says so.
    if (showModal && (rfq as any)?.pendingUpdate) {
      setMinDraft({});
      setShowModal(false);
      toast.add(`No changes made. ${rfqLabel(rfq)} is still ${(rfq as any).previousQuantity} ${unit || "yds"}.`, "var(--theme-ui-colors-success)");
      return;
    }
    if (showModal && !isCompany && values) {
      _updateIEOnClose(values);
    }
    setMinDraft({});
    setShowModal((prev) => !prev);
  };

  const _submitIEModal = async (values: any) => {
    const selectedService = values.services.find((service: any) => service.selected);

    // Install-only: a pickup/delivery choice is required before a quote can be
    // created, because the provider PO must carry supply instructions. Block
    // here (Create Quote path) if Install Only is selected without a choice.
    const submitIsInstallOnly = isInstallOnlyName(selectedService?.name);
    if (submitIsInstallOnly && !values.mulchSupplyPreference) {
      toast.add(
        "Select a mulch supply preference (pickup or delivery) before creating the quote.",
        "var(--theme-ui-colors-danger)"
      );
      return;
    }

    const mergedAddons = Array.isArray(values.services)
      ? values.services.flatMap((service: any) =>
          Array.isArray(service.addons)
            ? service.addons.map((addon: any) => ({
                id: addon.id,
                quantity: values[addon.id] ?? addon.quantity,
              }))
            : []
        )
      : [];

    // Fibar install-only: only persist a supply preference when the selected
    // service is Install Only; otherwise clear it.
    const isInstallOnly = isInstallOnlyName(selectedService?.name);

    const update: UpdateEstimateData = {
      projectName: values.projectName,
      quantity: values.quantity,
      serviceDate: values.startDate ? moment.unix(values.startDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : moment().add(30, "days").utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
      instructions: values.notes,
      addons: mergedAddons,
      mulchSupplyPreference: isInstallOnly ? (values.mulchSupplyPreference ?? null) : null,
    };

    if (isCompany) {
      update.primaryClientId = values.primaryClient ? values.primaryClient : client.value;
      if (selectedService) {
        update.selectServices = [selectedService.id];
      }
    } else {
      if (values.primaryClient) {
        update.primaryClientId = user.client.id;
      }
      if (selectedService) {
        update.selectServices = [selectedService.id];
      }
    }

    if (!rfq?.id) {
      console.warn("RFQ is missing, skipping update.");
      setRfqSubmitting(false);
      return;
    }

    const data = { id: rfq.id };
    const res = await updateEstimate({ data: data, update: update });

    if (values.notes) {
      const noteData = {
        note: values.notes,
        entityId: rfq?.quoteRequest.id,
        entityType: "QuoteRequest",
        private: false,
      };
      const noteRes = await createNote({ data: noteData });

      if (noteRes.id) {
        // Track quote request note creation from instant estimate
        void trackActivity({
          module: "quote-requests",
          action: "add_quote_request_note",
          actionType: "create",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "quote-request",
          entityId: rfq?.quoteRequest.id,
          metadata: {
            noteId: noteRes.id,
            source: "instant_estimate",
            private: false,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    }

    if (res.id) {
      const service = {
        selectServices: [selectedService.id],
      };

      const quoteRes = await generateQuoteFromEstimate({ data: { id: rfq.id }, service });

      // Track quote creation from instant estimate
      void trackActivity({
        module: "quotes",
        action: "create_quote",
        actionType: "create",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "quote",
        entityId: quoteRes.id,
        metadata: {
          source: "instant_estimate",
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });

      toast.add(
        (rfq as any)?.pendingUpdate
          ? `${rfqLabel(rfq)} updated to ${values.quantity} ${unit || "yds"} and quote generated.`
          : "Quote generated.",
        "var(--theme-ui-colors-success)"
      );
      navigate(`/quotes/${quoteRes.id}`);
    }
  };

  const _updateIEOnClose = async (values: any) => {
    if (!values || !values.services) return;

    setRfqSubmitting(true);

    const mergedAddons = Array.isArray(values.services)
      ? values.services.flatMap((service: any) =>
          Array.isArray(service.addons)
            ? service.addons.map((addon: any) => ({
                id: addon.id,
                quantity: values[addon.id] ?? addon.quantity,
              }))
            : []
        )
      : [];

    // Fibar install-only: mirror the supply-preference persistence used on submit.
    const closeSelectedService = values.services.find((service: any) => service.selected);
    const closeIsInstallOnly = isInstallOnlyName(closeSelectedService?.name);

    const update: UpdateEstimateData = {
      projectName: values.projectName,
      quantity: values.quantity,
      serviceDate: values.startDate ? moment.unix(values.startDate).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : moment().add(30, "days").utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
      instructions: values.notes,
      addons: mergedAddons,
      mulchSupplyPreference: closeIsInstallOnly ? (values.mulchSupplyPreference ?? null) : null,
    };

    if (isCompany) {
      update.primaryClientId = values.primaryClient ? values.primaryClient : client.value;
      const selectedService = values.services.find((service: any) => service.selected);
      if (selectedService) {
        update.selectServices = [selectedService.id];
      }
    } else {
      if (values.primaryClient) {
        update.primaryClientId = user.client.id;
      }
    }

    if (values.notes) {
      const noteData = {
        note: values.notes,
        entityId: rfq?.quoteRequest.id,
        entityType: "QuoteRequest",
        private: false,
      };
      const noteRes = await createNote({ data: noteData });

      if (noteRes.id) {
        // Track quote request note creation from instant estimate
        void trackActivity({
          module: "quote-requests",
          action: "add_quote_request_note",
          actionType: "create",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "quote-request",
          entityId: rfq?.quoteRequest.id,
          metadata: {
            noteId: noteRes.id,
            source: "instant_estimate",
            private: false,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }
    }

    if (!rfq?.id) {
      console.warn("RFQ is missing, skipping update.");
      setRfqSubmitting(false);
      return;
    }

    const data = { id: rfq.id };
    const res = await updateEstimate({ data: data, update: update });

    if (res.id) {
      // Track quote request update
      const changedFields: string[] = [];
      if (initialRfqValues) {
        if (update.projectName !== initialRfqValues.projectName) changedFields.push("projectName");
        if (update.quantity !== initialRfqValues.quantity) changedFields.push("quantity");
        if (update.serviceDate && update.serviceDate !== initialRfqValues.serviceDate) changedFields.push("serviceDate");
        if (update.instructions !== initialRfqValues.instructions) changedFields.push("instructions");
        if (update.primaryClientId !== initialRfqValues.primaryClientId) changedFields.push("primaryClientId");
        if (update.selectServices && update.selectServices[0] !== initialRfqValues.selectServices) changedFields.push("selectServices");
        if (update.addons && update.addons.length > 0) changedFields.push("addons");
      }

      if (changedFields.length > 0) {
        void trackActivity({
          module: "quote-requests",
          action: "update_quote_request",
          actionType: "update",
          userId: user?.id || null,
          companyId: user?.company?.id || null,
          entityType: "quote-request",
          entityId: rfq?.quoteRequest?.id || null,
          metadata: {
            updatedFields: changedFields,
          },
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        });
      }

      setRfqSubmitting(false);
      toast.add(
        (rfq as any)?.pendingUpdate
          ? `${rfqLabel(rfq)} updated: ${(rfq as any).previousQuantity} \u2192 ${values.quantity} ${unit || "yds"}.`
          : "RFQ updated successfully.",
        "var(--theme-ui-colors-success)"
      );
      setShowModal(false);
      triggerRefresh();
    } else {
      setRfqSubmitting(false);
      toast.add(res.message || "An error occurred while updating the estimate.", "var(--theme-ui-colors-danger)");
    }
  };

  return (
    <>
      {/* Mobile: compact trigger button */}
      <Box sx={{ display: ["flex", "none"] }}>
        <BasicButton type="button" scheme="secondaryOutline" onClick={() => setShowEstimateFormModal(true)} styles={{ whiteSpace: "nowrap" }}>
          <InstantEstimateIcon fill={"#000"} width="14px" height="14px" />
          New Estimate
        </BasicButton>
      </Box>

      {/* Desktop: inline form */}
      <Flex sx={{ display: ["none", "flex"], flexDirection: "row", alignItems: "center", border: `1px solid ${themevals.colors.inputOutline}`, padding: "10px", borderRadius: "6px", background: "#f8f8f8" }}>
        <Flex sx={{ flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0, marginRight: "14px" }}>
          <RFQIcon fill={"#000"} width="16px" height="16px" />
          <Box sx={{ marginTop: "4px", fontFamily: "Poppins", fontSize: "12px", fontWeight: "600", color: "#000", whiteSpace: "nowrap" }}>
            Create RFQ
          </Box>
        </Flex>
        <Formik initialValues={{ selectedProducts: [] }} onSubmit={_submitInstantEstimate} enableReinitialize>
          {({
            isSubmitting,
            values,
            setFieldValue,
            submitCount,
            errors,
          }: {
            isSubmitting: boolean;
            values: any;
            setFieldValue: (name: string, val?: any) => void;
            submitCount: number;
            errors: any;
          }) => {
            const toggleProduct = async (val: any) => {
              const fullProd = await getProduct({ data: { id: val } });
              setUnit(fullProd.unit);
              setBulkProduct(fullProd);
              setFieldValue("bulk_product", val);
            };

            return (
              <Form noValidate autoComplete="off">
                <MinimumWatcher isCompany={!!isCompany} onMin={setMinQtyYds} />
                <Flex sx={{ flexDirection: "row", alignItems: "flex-end" }}>
                  {isCompany && companyOptions && companyOptions.length > 0 && (
                    <Flex sx={{ maxWidth: "200px", width: "200px" }}>
                      <SelectInput
                        name="company"
                        label="Company"
                        value={values.company}
                        options={companyOptions}
                        onChange={(val: any) => {
                          setFieldValue("company", val);
                          const selectedCompany = companyOptions.find((option) => option.value === val);
                          setClient(selectedCompany);
                        }}
                        $errors={errors.company && submitCount > 0 ? errors.company : null}
                        $customStyles={{ width: "100%" }}
                      />
                    </Flex>
                  )}
                  <Flex sx={{ marginLeft: "10px", maxWidth: "200px", width: "200px" }}>
                    <GooglePlaces
                      label="Address"
                      name="googlePlace"
                      value={values.googlePlace}
                      $customStyles={{ width: "100%", marginBottom: "0px" }}
                      onChange={(val: any) => {
                        setFieldValue("address", val?.address);
                        setFieldValue("latitude", val?.latitude);
                        setFieldValue("longitude", val?.longitude);
                      }}
                      $errors={(errors?.address || errors?.latitude || errors?.longitude) && submitCount > 0 ? errors?.address ?? errors?.latitude ?? errors?.longitude : null}
                    />
                  </Flex>
                  <Flex sx={{ marginLeft: "10px", maxWidth: "200px", width: "200px" }}>
                    <SelectInput
                      name="bulk_product"
                      label="Product"
                      value={values.bulk_product}
                      options={productOptions}
                      onChange={(val: any) => toggleProduct(val)}
                      $errors={errors.bulk_product && submitCount > 0 ? errors.bulk_product : null}
                      $customStyles={{ width: "100%" }}
                    />
                  </Flex>
                  <Flex sx={{ marginLeft: "10px", maxWidth: "120px", width: "120px" }}>
                    <TextInput
                      name={"quantity"}
                      label="Quantity"
                      type="number"
                      value={values.quantity}
                      onChange={(val: any) => setFieldValue("quantity", val)}
                      suffix={unit !== "" ? unit : ""}
                      placeholder={minHint}
                      $errors={errors?.quantity && submitCount > 0 ? errors.quantity : null}
                      $customStyles={{ width: "100%", marginBottom: "0px" }}
                    />
                  </Flex>
                  <Flex sx={{ alignItems: "center" }}>
                    <BasicButton type="submit" $submitting={isSubmitting} styles={{ margin: "0 0 0 10px" }} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                      {!isSubmitting && <SubmitIcon fill={"#fff"} direction="up" />}
                      Estimate
                    </BasicButton>
                    {fuelActive && (
                      <span style={{ marginLeft: 10, fontFamily: "Poppins", fontSize: 11, color: themevals.colors.dkgrey, maxWidth: 130, lineHeight: "14px" }}>
                        Fuel surcharges may apply to quotes.
                      </span>
                    )}
                  </Flex>
                </Flex>
              </Form>
            );
          }}
        </Formik>
      </Flex>
      <Modal isOpen={showModal} toggle={() => toggleModal({})} centered size={"md"} closeOnBackdropClick={false}>
        {rfq && (
          <Formik
            initialValues={{
              services:
                // Install-only ordering: float an "Install Only" service to the
                // top so it's first in the list and selected by default. The
                // rest keep their original relative order. Everything downstream
                // keys off index 0 (default selected + quantity/total-units
                // reads), so sorting the source array here keeps all of that
                // consistent. No-op when there's no Install Only service.
                (rfq?.services ?? [])
                  .slice()
                  .sort((a, b) => {
                    const aInstall = isInstallOnlyName(a.name) ? 0 : 1;
                    const bInstall = isInstallOnlyName(b.name) ? 0 : 1;
                    return aInstall - bInstall;
                  })
                  .map((service: any, idx: number, arr: any[]) => ({
                    ...service,
                    // Default to the first option that meets its own minimum.
                    selected: idx === Math.max(0, arr.findIndex((x: any) => !x.belowMinimum)) && !service.belowMinimum,
                  })) || [],
            }}
            onSubmit={(values) => _submitIEModal(values)}
            enableReinitialize
          >
            {({
              isSubmitting,
              values,
              setFieldValue,
              submitCount,
              errors,
            }: {
              isSubmitting: boolean;
              values: any;
              setFieldValue: (name: string, val?: any) => void;
              submitCount: number;
              errors: any;
            }) => {
              // Quantity is editable on the results (2026-10-07, Jack): nothing
              // is saved until the client submits, so Cancel changes nothing.
              // Each option's own minimum is re-checked here; the server
              // checks again on submit.
              const setQty = (raw: any) => {
                const q = Math.max(0, Number(raw) || 0);
                const next = values.services.map((sv: any) => ({
                  ...sv,
                  quantity: q,
                  belowMinimum: sv.minQuantityYds != null && q < Number(sv.minQuantityYds),
                }));
                // Keep the selection valid: drop a now-below-minimum pick and
                // fall back to the first option that meets its minimum.
                if (!next.some((sv: any) => sv.selected && !sv.belowMinimum)) {
                  const firstOk = next.findIndex((sv: any) => !sv.belowMinimum);
                  next.forEach((sv: any, k: number) => { sv.selected = k === firstOk; });
                }
                setFieldValue("services", next);
                setFieldValue("quantity", q);
              };
              // Below-minimum option: set the request's quantity to what was
              // typed (at least the option's minimum) and select that option.
              // Applies to the whole request; nothing is saved until submit.
              const raiseTo = (svc: any, raw?: string) => {
                const min = Number(svc?.minQuantityYds);
                if (!min) return;
                const q = Math.round(Number(raw ?? min) || 0);
                if (q < min) {
                  toast.add(`${svc.name || "This option"} requires at least ${min} ${unit || "yds"}.`, "var(--theme-ui-colors-danger)");
                  return;
                }
                setFieldValue(
                  "services",
                  values.services.map((sv: any) => ({
                    ...sv,
                    quantity: q,
                    belowMinimum: sv.minQuantityYds != null && q < Number(sv.minQuantityYds),
                    selected: sv.id === svc.id,
                  }))
                );
                setFieldValue("quantity", q);
                setMinDraft({});
              };
              // Quantity the results opened with, for "changed from N · Undo".
              const openedQty = Number(rfq?.services?.[0]?.quantity ?? 0);

              const toggleCheckbox = (selectedIndex: number) => {
                if (values.services?.[selectedIndex]?.belowMinimum) return;
                setFieldValue(
                  "services",
                  values.services.map((service: any, idx: number) => (idx === selectedIndex ? { ...service, selected: !service.selected } : { ...service, selected: false }))
                );
              };

              useEffect(() => {
                if (!isCompany && companyClientOptions.length === 1) {
                  setFieldValue("primaryClient", companyClientOptions[0]?.value);
                }
              }, [companyClientOptions, isCompany]);

              const selectedService = values.services.find((service: any) => service.selected);
              const selectedAddons = selectedService?.addons?.slice().sort((a: any, b: any) => a.position - b.position) ?? [];

              useEffect(() => {
                if (values.services[0]?.quantity) {
                  setFieldValue("quantity", values.services[0]?.quantity);
                }
              }, [values.services, setFieldValue]);

              return (
                <Form noValidate autoComplete="off">
                  <ModalHeader toggle={() => toggleModal(values)}>
                    <ModalIcon>
                      <InstantEstimateIcon fill={"000"} />
                    </ModalIcon>
                    <ModalTitleWrapper>
                      <ModalTitle>Instant Estimate</ModalTitle>
                      <ModalSubtitle>{"Select a service and complete your project details to receive a quote."}</ModalSubtitle>
                    </ModalTitleWrapper>
                  </ModalHeader>
                  <ModalBody>
                    {(rfq as any)?.pendingUpdate && (
                      <Box sx={{ marginBottom: "14px", padding: "10px 14px", borderRadius: "6px", background: "#FAEEDA", color: "#633806", fontFamily: "Poppins", fontSize: "13px" }}>
                        <b>{`Updating ${rfqLabel(rfq)}`}</b>
                        {` \u2014 currently ${(rfq as any).previousQuantity} ${unit || "yds"}, changing to ${values.services?.[0]?.quantity ?? 0} ${unit || "yds"}. Nothing is saved until you click ${isCompany ? "Save RFQ or " : ""}Create quote. Cancel leaves it at ${(rfq as any).previousQuantity} ${unit || "yds"}.`}
                      </Box>
                    )}
                    <Flex sx={{ border: `1px solid ${themevals.colors.inputOutline}`, padding: "15px 20px", borderRadius: "6px" }}>
                      <Grid gap={"20px"} columns={[3, "1fr 2fr 1fr"]} sx={{ width: "100%" }}>
                        <Flex sx={{ flexDirection: "column", width: "100%", justifyContent: "center" }}>
                          <Flex sx={{ alignItems: "center", gap: "6px" }}>
                            <ClientsIcon fill={themevals.colors.dkgrey} width="18px" />
                            <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="0px">
                              Client
                            </Subtext>
                          </Flex>
                          <P marginTop="8px" marginBottom="0px" fontFamily="Poppins" fontSize="14px">
                            {client ? client.label : ""}
                          </P>
                        </Flex>
                        <Flex sx={{ flexDirection: "column", justifyContent: "center" }}>
                          <Flex sx={{ alignItems: "center", gap: "6px" }}>
                            <LocationIcon fill={themevals.colors.dkgrey} width="18px" />
                            <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="0px">
                              Address
                            </Subtext>
                          </Flex>
                          {rfq && rfq.location && rfq.location.address && (
                            <P marginTop="8px" marginBottom="0px" fontFamily="Poppins" fontSize="14px" textDecoration="underline">
                              {`${
                                rfq.location.address.line1 && rfq.location.address.line1 !== rfq.location.address.city
                                  ? rfq.location.address.line1 + (rfq.location.address.line2 ? `, ${rfq.location.address.line2}` : "")
                                  : rfq.location.address.line2
                                  ? rfq.location.address.line2
                                  : ""
                              }${(rfq.location.address.line1 && rfq.location.address.line1 !== rfq.location.address.city) || rfq.location.address.line2 ? ", " : ""}${rfq.location.address.city}, ${
                                rfq.location.address.state
                              }${rfq.location.address.postalCode ? ` ${rfq.location.address.postalCode}` : ""}`}
                            </P>
                          )}
                        </Flex>
                        <Flex sx={{ alignItems: "flex-end", justifyContent: "flex-end", flexDirection: "column" }}>
                          <Flex sx={{ flexDirection: "column", border: `1px solid ${themevals.colors.dkgrey}`, borderRadius: "6px", background: themevals.colors.inputOutline }}>
                            <Flex sx={{ padding: "10px", alignItems: "center", justifyContent: "center" }}>
                              <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" color={themevals.colors.dkgrey} fontSize="12px">
                                Total Units
                              </P>
                            </Flex>
                            <Flex sx={{ padding: "10px", borderTop: `1px solid ${themevals.colors.dkgrey}`, alignItems: "center", justifyContent: "center" }}>
                              <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="14px">
                                {`${values.services?.[0]?.quantity ?? 0} ${unit}`}.
                              </P>
                            </Flex>
                          </Flex>
                          {Number(values.services?.[0]?.quantity ?? 0) !== openedQty && (
                            <Flex sx={{ marginTop: "6px", gap: "6px", alignItems: "center", justifyContent: "flex-end" }}>
                              <Span fontFamily="Poppins" fontSize="11px" color={themevals.colors.dkgrey}>{`Changed from ${openedQty}`}</Span>
                              <Span fontFamily="Poppins" fontSize="11px" color={themevals.colors.black} textDecoration="underline" cursor="pointer" onClick={() => setQty(openedQty)}>Undo</Span>
                            </Flex>
                          )}
                        </Flex>
                      </Grid>
                    </Flex>
                    <Grid gap={"20px"} columns={[2]} sx={{ width: "100%" }}>
                      <Flex sx={{ flex: 1, marginTop: "20px", borderRight: `1px solid ${themevals.colors.inputOutline}`, padding: "0 15px 0 0", flexDirection: "column" }}>
                        <Flex sx={{ borderBottom: `1px solid ${themevals.colors.inputOutline}`, width: "100%", paddingBottom: "20px" }}>
                          <Flex sx={{ flex: 1, alignItems: "center" }}>
                            <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="16px" fontWeight="600">
                              {bulkProduct && bulkProduct.name ? bulkProduct.name : ""}
                            </P>
                          </Flex>
                          <Flex sx={{ flex: 1, justifyContent: "flex-end", alignItems: "center" }}>
                            <Subtext marginTop="0px" marginBottom="0px" fontFamily="Poppins">
                              Bulk Product
                            </Subtext>
                          </Flex>
                        </Flex>
                        {(() => {
                          // Rush order check: if service date is within 5 days, hide discount tiers
                          const isRushDate = values.startDate ? moment.unix(values.startDate).diff(moment().startOf("day"), "days") <= 5 : false;
                          const DISCOUNT_MINIMUM = 2000;
                          // N2 - a deposit-required client is not offered the
                          // early-payment incentive (mutually exclusive with the
                          // deposit). Read from the RFQ's client (present once a
                          // client is selected; a bare prospect with no client
                          // record just sees normal behavior). Mirrors the
                          // backend + Overview suppression.
                          const clientRequiresDeposit =
                            (rfq as any)?.client?.requiresDeposit === true ||
                            (rfq as any)?.primaryClient?.requiresDeposit === true;
                          return values.services.map((service: any, idx: number) => {
                          // Calculate discounted per yard and total for this service
                          const discountedPerYard = Number(service.price) * 0.97;
                          const largeDiscountPerYard = Number(service.price) * 0.95;
                          const discountedTotal = Number(service.quantity) * discountedPerYard;
                          const largeDiscountTotal = Number(service.quantity) * largeDiscountPerYard;
                          console.log("client", client);
                          return (
                            <Flex
                              sx={{
                                flexDirection: "row",
                                border: values.services?.[idx]?.selected
                                  ? "1px solid black"
                                  : `1px solid ${themevals.colors.inputOutline}`,
                                borderRadius: "6px",
                                marginTop: "12px",
                                ...(service.belowMinimum ? { background: "#FAFAF8" } : {}),
                              }}
                              key={idx}
                            >
                              <Flex sx={{ flexGrow: 0, padding: "0 15px", justifyContent: "center", alignItems: "center", borderRight: `1px solid ${themevals.colors.inputOutline}` }}>
                                <Checkbox name={`services[${idx}].selected`} checked={values.services?.[idx]?.selected || false} labelSize="14px" onChange={() => toggleCheckbox(idx)} />
                              </Flex>
                              <Flex sx={{ flexDirection: "row", flex: 1, padding: "12px 15px", gap: "14px", alignItems: "stretch" }}>
                                <Flex sx={{ flexDirection: "column", flex: 1, minWidth: "0" }}>
                                  <Flex sx={{ marginBottom: "6px" }}>
                                    <Span
                                      display="inline-block"
                                      backgroundColor={values.services && values.services[idx]?.selected ? themevals.colors.black : themevals.colors.inputOutline}
                                      borderStyle="solid"
                                      borderWidth="1px"
                                      borderColor={values.services && values.services[idx]?.selected ? themevals.colors.black : themevals.colors.dkgrey}
                                      borderRadius="50px"
                                      paddingTop="4px"
                                      paddingBottom="4px"
                                      paddingLeft="12px"
                                      paddingRight="12px"
                                      whiteSpace="nowrap"
                                      fontFamily="Poppins"
                                      fontSize="12px"
                                      fontWeight="600"
                                      color={values.services && values.services[idx]?.selected ? themevals.colors.white : themevals.colors.dkgrey}
                                    >
                                      {service.name || "Service"}
                                    </Span>
                                  </Flex>
                                  <P marginTop="0px" marginBottom="2px" fontFamily="Rubik" fontSize="18px" fontWeight="600">
                                    ${service.price}/yrd.
                                  </P>
                                  <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="0px" fontSize="12px">
                                    Est. total{" "}
                                    <Span fontFamily="Poppins" fontSize="12px" color={themevals.colors.black} fontWeight="600">
                                      $
                                      {(Number(service.quantity) * Number(service.price))
                                        .toFixed(2)
                                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                    </Span>
                                    {service.description ? ` · ${service.description}` : ""}
                                  </Subtext>
                                  {service.belowMinimum && (
                                    <Box sx={{ marginTop: "10px", padding: "10px 12px", borderRadius: "6px", background: "#FAEEDA" }}>
                                      <Span fontFamily="Poppins" fontSize="12px" fontWeight="600" color="#633806">
                                        {`${service.name || "This option"} requires at least ${service.minQuantityYds} ${unit || "yds"}.`}
                                      </Span>
                                      <Flex sx={{ alignItems: "center", gap: "8px", marginTop: "8px" }}>
                                        <input
                                          type="number"
                                          min={service.minQuantityYds}
                                          step={1}
                                          aria-label={`Quantity for ${service.name || "this option"}`}
                                          value={minDraft[service.id] ?? String(service.minQuantityYds)}
                                          onChange={(e) => setMinDraft((d) => ({ ...d, [service.id]: e.target.value }))}
                                          style={{ width: "70px", border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "4px", padding: "5px 6px", fontFamily: "Poppins", fontSize: "13px", textAlign: "right", background: "#fff" }}
                                        />
                                        <Span fontFamily="Poppins" fontSize="13px">{unit || "yds"}</Span>
                                        <BasicButton outline scheme="secondary" type="button" keepText onClick={() => raiseTo(service, minDraft[service.id] ?? String(service.minQuantityYds))}>
                                          Apply
                                        </BasicButton>
                                      </Flex>
                                      <Span display="block" marginTop="6px" fontFamily="Poppins" fontSize="11px" color="#633806">
                                        Changes the quantity for this whole request and selects this option.
                                      </Span>
                                    </Box>
                                  )}
                                </Flex>
                                {!isRushDate && !clientRequiresDeposit && (Number(service.quantity) * Number(service.price)) >= DISCOUNT_MINIMUM && (
                                  <Flex sx={{ flexDirection: "row", flex: "0 0 auto", width: "170px", alignItems: "center" }}>
                                    {/* Fixed-height divider between pricing and discount info -
                                        deliberately shorter than the card so it never reaches
                                        the service pill above. */}
                                    <Box sx={{ width: "1px", height: "56px", flexShrink: 0, backgroundColor: themevals.colors.inputOutline, marginRight: "12px" }} />
                                    <Flex sx={{ flexDirection: "column", flex: 1 }}>
                                      <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="6px" fontSize="11px">
                                        Pay deposit up front, save:
                                      </Subtext>
                                      <Flex sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "baseline", paddingTop: "3px", paddingBottom: "3px" }}>
                                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="12px">
                                          100% <Span fontFamily="Poppins" fontSize="12px" color={themevals.colors.success} fontWeight="600">&middot; 5% off</Span>
                                        </P>
                                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="600" color={themevals.colors.success}>
                                          $
                                          {largeDiscountTotal
                                            .toFixed(2)
                                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                        </P>
                                      </Flex>
                                      <Flex sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "baseline", borderTop: `1px solid ${themevals.colors.inputOutline}`, paddingTop: "3px", paddingBottom: "3px" }}>
                                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="12px">
                                          50% <Span fontFamily="Poppins" fontSize="12px" color={themevals.colors.success} fontWeight="600">&middot; 3% off</Span>
                                        </P>
                                        <P marginTop="0px" marginBottom="0px" fontFamily="Poppins" fontSize="13px" fontWeight="600" color={themevals.colors.success}>
                                          $
                                          {discountedTotal
                                            .toFixed(2)
                                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                        </P>
                                      </Flex>
                                    </Flex>
                                  </Flex>
                                )}
                              </Flex>
                            </Flex>
                          );
                        })})()}
                        <Flex sx={{ flexDirection: "column", marginTop: "30px" }}>
                          {/* loop through settings eventually to get these bullets */}
                          <P fontFamily="Poppins" marginTop="0px" marginBottom="10px" fontSize="12px" paddingLeft="10px" color={themevals.colors.dkgrey} position="relative">
                            <span style={{ position: "absolute", top: "0", left: "0" }}>*</span>Estimate is good for 30 days.
                          </P>
                          <P fontFamily="Poppins" marginTop="0px" marginBottom="10px" fontSize="12px" paddingLeft="10px" color={themevals.colors.dkgrey} position="relative">
                            <span style={{ position: "absolute", top: "0", left: "0" }}>*</span>Additional fees may apply if blown in location is greater than 200 ft from drop off.
                          </P>
                        </Flex>
                      </Flex>
                      {/* second column */}
                      <Flex sx={{ paddingTop: "20px", flexDirection: "column", alignItems: "flex-start" }}>
                        <Flex sx={{ width: "100%", flex: "0 1 auto", alignItems: "flex-start" }}>
                          <Flex sx={{ width: "100%", flex: 1, justifyContent: "flex-start" }}>
                            <P fontFamily="Poppins" marginTop="0px" marginBottom="0px" fontSize="14px" fontWeight="600">
                              Project Details
                            </P>
                          </Flex>
                          <Flex sx={{ width: "100%", flex: 1, justifyContent: "flex-end" }}>
                            <P fontFamily="Poppins" marginTop="0px" marginBottom="0px" fontSize="14px" color={themevals.colors.dkgrey} fontWeight="600">
                              RFQ#{` GMR-${String(rfq.quoteRequest.sequence).padStart(5, "0")}`}
                            </P>
                          </Flex>
                        </Flex>
                        <Subtext fontFamily="Poppins" marginTop="6px" marginBottom="0px" fontSize="12px">
                          Optional unless marked *. Add what you know now; you can finish these later.
                        </Subtext>
                        {/* start form */}
                        <Flex sx={{ width: "100%", marginTop: "20px" }}>
                          <TextInput
                            name="projectName"
                            label="Project Name (optional)"
                            placeholder="e.g. Oak Park - north playground"
                            type="text"
                            value={values.projectName || ""}
                            onChange={(val: any) => setFieldValue("projectName", val)}
                            $errors={errors?.projectName && submitCount > 0 ? errors.projectName : null}
                          />
                        </Flex>
                        <Flex sx={{ width: "100%" }}>
                          <SelectDate
                            name="startDate"
                            label="Estimated Service Date (optional)"
                            description="Not sure yet? Leave it - we plan about 30 days out and confirm with you."
                            value={values.startDate}
                            onChange={(val: any) => setFieldValue("startDate", val)}
                            $errors={errors.startDate && submitCount > 0 ? errors.startDate : null}
                            $customStyles={{ width: "100%" }}
                          />
                        </Flex>
                        {values.startDate && moment.unix(values.startDate).diff(moment().startOf("day"), "days") <= 5 && (
                          <Flex sx={{
                            backgroundColor: "#fff3cd",
                            border: "1px solid #ffc107",
                            borderRadius: "6px",
                            padding: "10px 14px",
                            fontFamily: "Poppins",
                            fontSize: "13px",
                            marginTop: "0px",
                            marginBottom: "10px",
                            alignItems: "center",
                            gap: "8px",
                            width: "100%"
                          }}>
                            <Box sx={{ fontSize: "16px", flexShrink: 0 }}>{"\u26A0\uFE0F"}</Box>
                            <Box sx={{ color: "#856404", fontWeight: "500" }}>
                              Rush Order: Service date is within 5 days. This order will not be eligible for early payment discounts.
                            </Box>
                          </Flex>
                        )}
                        <Flex sx={{ width: "100%", marginTop: "30px", marginBottom: "30px" }}>
                          <SelectInput
                            name="primaryClient"
                            label="Primary Client"
                            value={values.primaryClient}
                            options={companyClientOptions}
                            onChange={(val: any) => setFieldValue("primaryClient", val)}
                            $errors={errors.primaryClient && submitCount > 0 ? errors.primaryClient : null}
                            $customStyles={{ width: "100%" }}
                            readonly={!isCompany}
                          />
                        </Flex>
                        {/* Fibar install-only: mulch supply preference. Shows only when the
                            selected service is Install Only, since only then does Fibar
                            supply the mulch and a pickup/delivery choice applies. */}
                        {isInstallOnlyName(selectedService?.name) && (
                          <Flex
                            sx={{
                              width: "100%",
                              flexDirection: "column",
                              marginBottom: "30px",
                              border: `1px solid ${
                                submitCount > 0 && !values.mulchSupplyPreference
                                  ? themevals.colors.danger
                                  : themevals.colors.black
                              }`,
                              borderRadius: "6px",
                              padding: "14px 16px",
                              background: themevals.colors.base_100,
                            }}
                          >
                            <P fontFamily="Poppins" fontSize="12px" fontWeight="600" marginTop="0px" marginBottom="4px">
                              Mulch supply preference <Span fontFamily="Poppins" fontSize="12px" color={themevals.colors.danger}>*</Span>
                            </P>
                            <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="12px" fontSize="11px">
                              The mulch on this service is supplied by a third party. Choose how it reaches the installer.
                            </Subtext>
                            <Flex sx={{ gap: "10px" }}>
                              {[
                                { key: "pickup", label: "Pickup", sub: "Installer collects from the supplier's nearest depot." },
                                { key: "delivery", label: "Delivery", sub: "Supplier delivers; we route to yard or site." },
                              ].map((opt) => {
                                const on = values.mulchSupplyPreference === opt.key;
                                return (
                                  <Box
                                    key={opt.key}
                                    onClick={() => setFieldValue("mulchSupplyPreference", opt.key)}
                                    sx={{
                                      flex: 1,
                                      cursor: "pointer",
                                      background: "#fff",
                                      border: `1px solid ${on ? themevals.colors.black : themevals.colors.mdgrey}`,
                                      borderRadius: "6px",
                                      padding: "10px 12px",
                                    }}
                                  >
                                    <Flex sx={{ alignItems: "center", gap: "8px" }}>
                                      <Box
                                        sx={{
                                          width: "15px",
                                          height: "15px",
                                          borderRadius: "50%",
                                          flex: "0 0 auto",
                                          border: `1.5px solid ${on ? themevals.colors.black : themevals.colors.mdgrey}`,
                                          background: on
                                            ? "radial-gradient(" + themevals.colors.black + " 42%, #fff 46%)"
                                            : "#fff",
                                        }}
                                      />
                                      <P fontFamily="Poppins" fontSize="13px" fontWeight="500" marginTop="0px" marginBottom="0px">
                                        {opt.label}
                                      </P>
                                    </Flex>
                                    <Subtext fontFamily="Poppins" marginTop="5px" marginBottom="0px" fontSize="11px">
                                      {opt.sub}
                                    </Subtext>
                                  </Box>
                                );
                              })}
                            </Flex>
                          </Flex>
                        )}
                        {selectedAddons.map((addon: any, idx: number) => (
                          <Flex sx={{ width: "100%", marginTop: "0px", marginBottom: "0px" }} key={idx}>
                            <TextInput
                              name={addon.id}
                              label={`${addon.name} (optional)`}
                              placeholder="Leave blank if not needed"
                              type="number"
                              value={values[addon.id] || ""}
                              onChange={(val: any) => setFieldValue(addon.id, val)}
                              suffix={addon.unit}
                              $errors={errors[addon.id] && submitCount > 0 ? errors[addon.id] : null}
                            />
                          </Flex>
                        ))}
                        <Flex sx={{ width: "100%", marginTop: "0px" }}>
                          <TextInput
                            name="notes"
                            label="Special Instructions (optional)"
                            placeholder="Gate codes, access hours, where to blow the mulch, anything the crew should know."
                            rows={3}
                            type="textarea"
                            value={values.notes || ""}
                            onChange={(val: any) => setFieldValue("notes", val)}
                            $errors={errors.notes && submitCount > 0 ? errors.notes : null}
                            $customStyles={{ width: "100%" }}
                          />
                        </Flex>
                      </Flex>
                    </Grid>
                  </ModalBody>
                  <ModalFooter fullFooter={true}>
                    <Flex sx={{ width: "100%", flex: 1 }}>
                      <Button variant="secondaryOutline" type="button" onClick={() => toggleModal(values)} style={{ marginRight: "10px" }}>
                        Cancel
                      </Button>
                    </Flex>
                    <Flex sx={{ width: "100%", flex: 1, justifyContent: "flex-end" }}>
                      {isCompany && (
                        <BasicButton
                          styles={{ marginRight: "20px" }}
                          type="button"
                          $submitting={rfqSubmitting}
                          $iconHeight="14px"
                          $iconWidth="14px"
                          $loadingColor="#000"
                          keepText={true}
                          scheme="secondaryOutline"
                          onClick={() => _updateIEOnClose(values)}
                        >
                          {!rfqSubmitting && <CheckMarkIcon fill={"#000"} />}
                          Save RFQ
                        </BasicButton>
                      )}
                      <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <QuotesIcon fill={"#fff"} />}
                        Create quote
                      </BasicButton>
                    </Flex>
                  </ModalFooter>
                </Form>
              );
            }}
          </Formik>
        )}
      </Modal>
      {/* An open work order exists for this client at this site (staff). */}
      <Modal isOpen={!!woPrompt} toggle={() => !woBusy && setWoPrompt(null)} centered size={"sm"} closeOnBackdropClick={false}>
        <ModalHeader toggle={() => !woBusy && setWoPrompt(null)}>
          <ModalTitleWrapper>
            <ModalTitle>{(woPrompt?.matches?.length ?? 0) > 1 ? `${woPrompt?.matches.length} open work orders at this site` : "Open work order at this site"}</ModalTitle>
            <ModalSubtitle>{"Price the work order so the RFQ is attached to it, and the plan and Corrigo see it."}</ModalSubtitle>
          </ModalTitleWrapper>
        </ModalHeader>
        <ModalBody>
          <Box sx={{ maxHeight: "260px", overflowY: "auto", paddingRight: "4px" }}>
            {(woPrompt?.matches ?? []).map((m: any) => {
              const picked = woPick === m.id;
              return (
                <Box
                  key={m.id}
                  onClick={() => setWoPick(m.id)}
                  sx={{
                    display: "flex", gap: "10px", alignItems: "flex-start", textAlign: "left", padding: "8px 10px", marginBottom: "6px",
                    border: `1px solid ${picked ? "#111" : "#E4E7EB"}`, borderRadius: "6px", cursor: "pointer", fontFamily: "Poppins", fontSize: "13px",
                  }}
                >
                  <input type="radio" readOnly checked={picked} style={{ marginTop: "3px", width: "auto", flex: "0 0 auto" }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box>{[m.externalId ? `WO ${m.externalId}` : "Work order", m.siteName].filter(Boolean).join(" \u00b7 ")}</Box>
                    <Box sx={{ fontSize: "11px", color: themevals.colors.dkgrey }}>
                      {[m.planName, m.receivedAt ? `received ${moment(m.receivedAt).format("MMM D")}` : null].filter(Boolean).join(" \u00b7 ")}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
          <P fontFamily={"Poppins"} fontSize={"12px"} color={themevals.colors.dkgrey} marginBottom={0}>
            {`Pricing it generates the RFQ from the work order at ${woPrompt?.values?.quantity ?? ""} ${unit || "yds"}. Choose "Not for this work order" only if this is separate work at the same site.`}
          </P>
        </ModalBody>
        <ModalFooter>
          <BasicButton outline scheme="secondary" type="button" keepText disabled={woBusy} onClick={() => setWoPrompt(null)}>Cancel</BasicButton>
          <BasicButton outline scheme="secondary" type="button" keepText disabled={woBusy} onClick={() => {
            const p = woPrompt;
            setWoPrompt(null);
            if (p) _submitInstantEstimate({ ...p.values, workOrderAction: "separate" }, p.actions);
          }}>Not for this work order</BasicButton>
          <BasicButton type="button" keepText disabled={!woPick || woBusy} $submitting={woBusy} onClick={async () => {
            const p = woPrompt;
            const id = woPick;
            if (!p || !id) return;
            setWoBusy(true);
            const res: any = await convertWorkOrder({ data: { id }, overrides: { quantity: Number(p.values?.quantity), ...(p.values?.bulk_product ? { productId: p.values.bulk_product } : {}) } });
            setWoBusy(false);
            if (res?.rfqId) {
              setWoPrompt(null);
              toast.add("RFQ generated from the work order and attached to it.", "var(--theme-ui-colors-success)");
              navigate(`/maintenance/work-orders/${id}`);
            } else if (res?.error === "Already converted") {
              setWoPrompt(null);
              toast.add("That work order already has an RFQ.", "var(--theme-ui-colors-warning)");
              navigate(`/maintenance/work-orders/${id}`);
            } else {
              toast.add(res?.message || "Could not generate the RFQ for this work order.", "var(--theme-ui-colors-danger)");
            }
          }}>Price work order</BasicButton>
        </ModalFooter>
      </Modal>
      {/* An open RFQ already exists for this organization + site + product. */}
      <Modal isOpen={!!dupPrompt} toggle={() => setDupPrompt(null)} centered size={"sm"} closeOnBackdropClick={false}>
        <ModalHeader toggle={() => setDupPrompt(null)}>
          <ModalTitleWrapper>
            <ModalTitle>{(dupPrompt?.matches?.length ?? 0) > 1 ? `${dupPrompt?.matches.length} open RFQs already exist` : "An open RFQ already exists"}</ModalTitle>
            <ModalSubtitle>{"Your organization already requested this product at this location."}</ModalSubtitle>
          </ModalTitleWrapper>
        </ModalHeader>
        <ModalBody>
          {(dupPrompt?.matches?.length ?? 0) > 1 && (
            <Box sx={{ marginBottom: "12px", maxHeight: "260px", overflowY: "auto", paddingRight: "4px" }}>
              {(dupPrompt?.matches ?? []).map((m: any) => {
                const canPick = m.updatable !== false;
                const picked = dupPick === m.estimateId;
                return (
                  <Box
                    key={m.estimateId}
                    onClick={() => canPick && setDupPick(m.estimateId)}
                    sx={{
                      display: "flex", gap: "10px", alignItems: "flex-start", justifyContent: "flex-start", textAlign: "left", padding: "8px 10px", marginBottom: "6px",
                      border: `1px solid ${picked ? "#111" : "#E4E7EB"}`, borderRadius: "6px",
                      cursor: canPick ? "pointer" : "default", opacity: canPick ? 1 : 0.6, fontFamily: "Poppins", fontSize: "13px",
                    }}
                  >
                    <input type="radio" readOnly checked={picked} disabled={!canPick} style={{ marginTop: "3px", width: "auto", flex: "0 0 auto" }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box>
                        {[
                          m.sequence != null ? `#${m.sequence}` : null,
                          m.quantity != null ? `${m.quantity} ${unit || "yds"}` : null,
                          m.createdBy ? `by ${m.createdBy}` : null,
                        ].filter(Boolean).join(" \u00b7 ")}
                      </Box>
                      <Box sx={{ fontSize: "11px", color: themevals.colors.dkgrey }}>
                        {[m.createdAt ? moment(m.createdAt).format("MMM D, h:mm a") : null, canPick ? null : "quoted, waiting on your answer - review the quote instead"].filter(Boolean).join(" \u00b7 ")}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
          {(dupPrompt?.matches?.length ?? 0) <= 1 && dupPrompt?.existing && (
            <P fontFamily={"Poppins"} fontSize={"13px"} marginTop={0}>
              {[
                dupPrompt.existing.projectName || dupPrompt.existing.product,
                dupPrompt.existing.quantity != null ? `${dupPrompt.existing.quantity} ${unit || "yds"}` : null,
                dupPrompt.existing.createdBy ? `by ${dupPrompt.existing.createdBy}` : null,
                dupPrompt.existing.createdAt ? moment(dupPrompt.existing.createdAt).format("MMM D, h:mm a") : null,
              ].filter(Boolean).join(" \u00b7 ")}
            </P>
          )}
          <P fontFamily={"Poppins"} fontSize={"12px"} color={themevals.colors.dkgrey} marginBottom={0}>
            {!dupPick
              ? "A quote has already been generated, so it can't be updated here. Create a separate RFQ, or cancel and work from the existing quote."
              : (dupPrompt?.matches?.length ?? 0) > 1
                ? `Pick the one to update to ${dupPrompt?.values?.quantity} ${unit || "yds"}, or create a separate RFQ (for example, another play area).`
                : `Update it to ${dupPrompt?.values?.quantity} ${unit || "yds"}, or create a separate RFQ (for example, a second play area).`}
          </P>
        </ModalBody>
        <ModalFooter>
          <BasicButton outline scheme="secondary" type="button" keepText onClick={() => setDupPrompt(null)}>Cancel</BasicButton>
          <BasicButton outline scheme="secondary" type="button" keepText onClick={() => {
            const p = dupPrompt;
            setDupPrompt(null);
            if (p) _submitInstantEstimate(p.values, p.actions, { duplicateAction: "new" });
          }}>Create new</BasicButton>
          {!!dupPick && (
            <BasicButton type="button" keepText onClick={() => {
              const p = dupPrompt;
              const target = dupPick;
              setDupPrompt(null);
              if (p && target) _submitInstantEstimate(p.values, p.actions, { duplicateAction: "update", existingEstimateId: target });
            }}>{(dupPrompt?.matches?.length ?? 0) > 1 ? "Update selected" : "Update existing"}</BasicButton>
          )}
        </ModalFooter>
      </Modal>
      {/* Mobile: estimate form modal */}
      <Modal isOpen={showEstimateFormModal} toggle={() => setShowEstimateFormModal(false)} centered size={"sm"} closeOnBackdropClick={false}>
        <Formik
          initialValues={{ selectedProducts: [] }}
          onSubmit={(values, actions) => {
            setShowEstimateFormModal(false);
            _submitInstantEstimate(values, actions);
          }}
          enableReinitialize
        >
          {({
            isSubmitting, values, setFieldValue, submitCount, errors,
          }: {
            isSubmitting: boolean; values: any;
            setFieldValue: (name: string, val?: any) => void;
            submitCount: number; errors: any;
          }) => {
            const toggleProduct = async (val: any) => {
              const fullProd = await getProduct({ data: { id: val } });
              setUnit(fullProd.unit);
              setBulkProduct(fullProd);
              setFieldValue("bulk_product", val);
            };
            return (
              <Form noValidate autoComplete="off">
                <MinimumWatcher isCompany={!!isCompany} onMin={setMinQtyYds} />
                <ModalHeader toggle={() => setShowEstimateFormModal(false)}>
                  <ModalIcon><InstantEstimateIcon fill={"000"} /></ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>New Estimate</ModalTitle>
                    <ModalSubtitle>{"Fill in the details below to generate an instant estimate."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <Flex sx={{ flexDirection: "column", gap: "0px" }}>
                    {isCompany && companyOptions && companyOptions.length > 0 && (
                      <SelectInput name="company" label="Company" value={values.company} options={companyOptions}
                        onChange={(val: any) => { setFieldValue("company", val); const selectedCompany = companyOptions.find((option: any) => option.value === val); setClient(selectedCompany); }}
                        $errors={errors.company && submitCount > 0 ? errors.company : null} $customStyles={{ width: "100%" }} />
                    )}
                    <GooglePlaces label="Address" name="googlePlace" value={values.googlePlace}
                      $customStyles={{ width: "100%", marginBottom: "0px" }}
                      onChange={(val: any) => { setFieldValue("address", val?.address); setFieldValue("latitude", val?.latitude); setFieldValue("longitude", val?.longitude); }}
                      $errors={(errors?.address || errors?.latitude || errors?.longitude) && submitCount > 0 ? errors?.address ?? errors?.latitude ?? errors?.longitude : null} />
                    <SelectInput name="bulk_product" label="Product" value={values.bulk_product} options={productOptions}
                      onChange={(val: any) => toggleProduct(val)}
                      $errors={errors.bulk_product && submitCount > 0 ? errors.bulk_product : null} $customStyles={{ width: "100%" }} />
                    <TextInput name={"quantity"} label="Quantity" type="number" value={values.quantity}
                      onChange={(val: any) => setFieldValue("quantity", val)} suffix={unit !== "" ? unit : ""} placeholder={minHint}
                      $errors={errors?.quantity && submitCount > 0 ? errors.quantity : null} $customStyles={{ width: "100%", marginBottom: "0px" }} />
                  </Flex>
                </ModalBody>
                <ModalFooter>
                  {fuelActive && (
                    <span style={{ marginRight: "auto", fontFamily: "Poppins", fontSize: 11, color: themevals.colors.dkgrey }}>
                      Fuel surcharges may apply to quotes.
                    </span>
                  )}
                  <Button variant="secondaryOutline" type="button" onClick={() => setShowEstimateFormModal(false)} style={{ marginRight: "10px" }}>Cancel</Button>
                  <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                    {!isSubmitting && <SubmitIcon fill={"#fff"} direction="up" />}
                    Estimate
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default InstantEstimateTool;
